import React, { useState, useEffect } from 'react';
import Footerdiv from "../layout/footer.js";
import Headerdiv from "../layout/header.js";
import {
  Layout,
  Typography,
  Descriptions,
  Card,
  Button,
  Space,
  Breadcrumb,
  Spin,
  Tag,
  Divider,
  Row,
  Col,
  Table,
  theme,
  Modal
} from 'antd'; // Import Link from react-router-dom
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { MAINAPIURL, APPBACKGROUND_COLOR } from '../appsettings.js';
import { AlertError, AlertSuccess } from '../usableclasses.js';

const { Title } = Typography;
const { Content } = Layout;

const PurchaseOrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const ApiURL = MAINAPIURL;
  const TOKEN = localStorage.getItem("AUTHCODE");
  const UPDATED_BY = localStorage.getItem("USERID");

  const { token: { colorBgContainer, borderRadiusLG }, } = theme.useToken();

  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [poItems, setPoItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);

  // Format date helper function
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (e) {
      return dateString;
    }
  };

  const handleDownload = async(filename) => {
		if(filename)
		{
			
            try {
                const response = await axios.post(`${ApiURL}/purchase/downloadfileinpo`,{ PoId: id, fileName: filename, downloadedBy: UPDATED_BY },
                    {
                        responseType: "blob", // Correct: Tells Axios to expect a Blob response
                        headers: {
                            SystemAccessToken: TOKEN, 
                        },
                    }
                );

                // Create a Blob from the response data
                const pdfBlob = new Blob([response.data]);

                // Create a temporary URL for the Blob
                const url = window.URL.createObjectURL(pdfBlob);

                // Create a temporary <a> element to trigger the download
                const tempLink = document.createElement("a");
                tempLink.href = url;
                tempLink.setAttribute(
                "download",
                filename
                ); // Set the desired filename for the downloaded file

                // Append the <a> element to the body and click it to trigger the download
                document.body.appendChild(tempLink);
                tempLink.click();

                // Clean up the temporary elements and URL
                document.body.removeChild(tempLink);
                window.URL.revokeObjectURL(url);

                AlertSuccess("file downloaded successfully.");

             
            } catch (error) {

                AlertError("Failed to download file.");

            } finally {
                
            }

			
		}
		else
		{
            AlertError("Please select a file before downloading.");
		}
	};

  useEffect(() => {
    const fetchPurchaseOrderDetails = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${ApiURL}/purchase/getpoheaderdetails/${id}`, {
          headers: { SystemAccessToken: TOKEN }
        });

        if (response.data) {
          setPurchaseOrder(response.data);
        } else {
          AlertError('Failed to load purchase order data');
        }
      } catch (error) {

        AlertError('Error loading purchase order details');
      } finally {
        setLoading(false);
      }
    };

    const fetchPurchaseOrderItems = async () => {
      setItemsLoading(true);
      try {
        const response = await axios.get(`${ApiURL}/purchase/getpoitemdetails/${id}`, {
          headers: { SystemAccessToken: TOKEN }
        });

        if (response.data) {
          setPoItems(Array.isArray(response.data) ? response.data : []);
        } else {
          AlertError('Failed to load purchase order items');
        }
      } catch (error) {

        AlertError('Error loading purchase order items');
      } finally {
        setItemsLoading(false);
      }
    };

    if (id) {
      fetchPurchaseOrderDetails();
      fetchPurchaseOrderItems();
    }
  }, [id, ApiURL, TOKEN]);

  const handleBack = () => {
    navigate('/polist');
  };

  const showApproveModal = () => {
    setApproveModalVisible(true);
  };

  const showRejectModal = () => {
    setRejectModalVisible(true);
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      // Replace with your actual API endpoint for approval
      await axios.post(`${ApiURL}/purchase/approval`, {
        poId: id,
        status: 'APPROVED',
        updatedBy: localStorage.getItem("USERID")
      }, {
        headers: { SystemAccessToken: TOKEN }
      });

      // Update local state
      setPurchaseOrder(prev => ({
        ...prev,
        currentStage: 'APPROVED'
      }));

      AlertSuccess('Purchase order approved successfully');
      setApproveModalVisible(false);
      navigate('/polist');

    } catch (error) {
      
      AlertError('Failed to approve purchase order');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    setRejecting(true);
    try {
     
      await axios.post(`${ApiURL}/purchase/approval`, {
        poId: id,
        status: 'REJECTED',
        updatedBy: localStorage.getItem("USERID")
      }, {
        headers: { SystemAccessToken: TOKEN }
      });
      
    
      setPurchaseOrder(prev => ({
        ...prev,
        currentStage: 'REJECTED'
      }));
      
      AlertSuccess('Purchase order rejected');
      setRejectModalVisible(false);
      navigate('/polist');

    } catch (error) {
      
      AlertError('Failed to reject purchase order');
    } finally {
      setRejecting(false);
    }
  };

  // Helper function to get status badge
  const getStatusBadge = (status) => {
    switch ((status || '').toUpperCase()) {
      case 'DRAFT':
        return <Tag color="orange">Draft</Tag>;
      case 'PENDING':
        return <Tag color="blue">Pending</Tag>;
      case 'APPROVED':
        return <Tag color="green">Approved</Tag>;
      case 'REJECTED':
        return <Tag color="red">Rejected</Tag>;
      case 'DELETED':
        return <Tag color="red">Deleted</Tag>;
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };

  // Table columns definition for items
  const columns = [
    {
      title: <strong>Code</strong>,
      dataIndex: 'itemSku',
      key: 'itemSku',
    },
    {
      title: <strong>Item Name</strong>,
      dataIndex: 'itemName',
      key: 'itemName',
    },
    {
      title: <strong>Quantity</strong>,
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: <strong>Cost Price</strong>,
      dataIndex: 'costPrice',
      key: 'costPrice',
      render: (price) => `${parseFloat(price).toLocaleString()}`
    },
    {
      title: <strong>Total Cost</strong>,
      dataIndex: 'totalCost',
      key: 'totalCost',
      render: (cost) => `${parseFloat(cost).toLocaleString()}`
    }
  ];

  return (
    <Layout style={{ backgroundColor: APPBACKGROUND_COLOR }}>
      <Headerdiv />
      <Content style={{ padding: '10px' }}>

        <Breadcrumb style={{ margin: "16px 0" }}>
            <Breadcrumb.Item><Link to="/polist">Purchase Orders</Link></Breadcrumb.Item>
            <Breadcrumb.Item>Purchase Order View</Breadcrumb.Item>
        </Breadcrumb>

        <div style={{ padding: 20, minHeight: '65vh', background: colorBgContainer, borderRadius: borderRadiusLG }}>
            <Space direction="vertical" style={{ width: "100%", padding: "15px" }}>
              
              {loading ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <Spin size="large" />
                  <div style={{ marginTop: 16 }}>Loading purchase order details...</div>
                </div>
              ) : purchaseOrder ? (
                <>
                  <div style={{ overflowX: 'auto' }}>
                    <Descriptions 
                      bordered 
                      column={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}
                      title="Basic Information"
                      layout="vertical"
                      size="small"
                    >
                      <Descriptions.Item label={<strong>PO Reference</strong>}>{purchaseOrder.poReferance}</Descriptions.Item>
                      <Descriptions.Item label={<strong>Create Date</strong>}>{formatDate(purchaseOrder.createDate)}</Descriptions.Item>
                      <Descriptions.Item label={<strong>Month</strong>}>{purchaseOrder.sysMonth}</Descriptions.Item>
                      <Descriptions.Item label={<strong>Year</strong>}>{purchaseOrder.sysYear}</Descriptions.Item>
                      <Descriptions.Item label={<strong>Financial Year</strong>}>{purchaseOrder.finYear}</Descriptions.Item>
                      
                      <Descriptions.Item label={<strong>Status</strong>}>{getStatusBadge(purchaseOrder.currentStage)}</Descriptions.Item>
                      <Descriptions.Item label={<strong>PO Value</strong>}>{parseFloat(purchaseOrder.totalCost || 0).toLocaleString()}</Descriptions.Item>
                      <Descriptions.Item label={<strong>Vendor</strong>}>{purchaseOrder.vendorName}</Descriptions.Item>
                      <Descriptions.Item label={<strong>Vendor Bill No</strong>}>{purchaseOrder.vendorBillNo}</Descriptions.Item>
                      
                      <Descriptions.Item label={<strong>Created By</strong>}>{purchaseOrder.createdBy}</Descriptions.Item>
                      <Descriptions.Item label={<strong>PO Note</strong>} >
                          <div dangerouslySetInnerHTML={{ __html: purchaseOrder.poNote }} />
                      </Descriptions.Item>
                      <Descriptions.Item label={<strong>Attachment</strong>}>
                          {purchaseOrder.poAttachmentFile == null ? (
                              <p style={{fontStyle:"italic"}}>No Attachments</p>
                          ) : (
                              <Button color="purple" variant="outlined"
                              style={{ marginTop: "8px",isplay: "flex", justifyContent: "flex-end" }}
                              onClick={() => handleDownload(purchaseOrder.poAttachmentFile)}>
                              Download File
                          </Button>
                          )}
                      </Descriptions.Item>
                    </Descriptions>
                  </div>
                  
                  <Divider />
                  
                  <Title level={5}>Items</Title>
                  
                  {itemsLoading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Spin />
                      <div>Loading items...</div>
                    </div>
                  ) : poItems && poItems.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                      <Table 
                        dataSource={poItems} 
                        columns={columns} 
                        rowKey="productId"
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                        summary={() => (
                          <Table.Summary.Row style={{ backgroundColor: "#f0f2f5", fontWeight: "bold" }}>
                            <Table.Summary.Cell index={0} colSpan={2}><b>Total</b></Table.Summary.Cell>
                            <Table.Summary.Cell index={1}>
                              <b>{purchaseOrder.totalQty || poItems.reduce((sum, item) => sum + parseInt(item.quantity || 0), 0)}</b>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2} />
                            <Table.Summary.Cell index={3}>
                              <b>{parseFloat(purchaseOrder.totalProductCost || 0).toLocaleString()}</b>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        )}
                      />
                    </div>
                  ) : (
                    <p>No items found for this purchase order.</p>
                  )}
                  
                  <Divider />
                  
                  <Row>
                    <Col xs={24} md={18} lg={12} style={{ marginLeft: 'auto' }}>
                      <Card size="small" title="Summary">
                        <Row style={{ margin: '8px 0' }}>
                          <Col span={12}><strong>Total Product Cost:</strong></Col>
                          <Col span={12} style={{ textAlign: 'right' }}>
                            {parseFloat(purchaseOrder.totalProductCost || 0).toLocaleString()}
                          </Col>
                        </Row>
                        <Row style={{ margin: '8px 0' }}>
                          <Col span={12}><strong>Total Vat (+):</strong></Col>
                          <Col span={12} style={{ textAlign: 'right' }}>
                            {parseFloat(purchaseOrder.totalVat || 0).toLocaleString()}
                          </Col>
                        </Row>
                        <Row style={{ margin: '8px 0' }}>
                          <Col span={12}><strong>Total Taxes (+):</strong></Col>
                          <Col span={12} style={{ textAlign: 'right' }}>
                            {parseFloat(purchaseOrder.totalTax || 0).toLocaleString()}
                          </Col>
                        </Row>
                        <Row style={{ margin: '8px 0' }}>
                          <Col span={12}><strong>Total Discount (-):</strong></Col>
                          <Col span={12} style={{ textAlign: 'right' }}>
                            {parseFloat(purchaseOrder.totalDiscount || 0).toLocaleString()}
                          </Col>
                        </Row>
                        <Row style={{ margin: '8px 0' }}>
                          <Col span={12}><strong>Total Local Shipping (+):</strong></Col>
                          <Col span={12} style={{ textAlign: 'right' }}>
                            {parseFloat(purchaseOrder.totalShippingLocal || 0).toLocaleString()}
                          </Col>
                        </Row>
                        <Row style={{ margin: '8px 0' }}>
                          <Col span={12}><strong>Total Int. Shipping (+):</strong></Col>
                          <Col span={12} style={{ textAlign: 'right' }}>
                            {parseFloat(purchaseOrder.totalShippingInt || 0).toLocaleString()}
                          </Col>
                        </Row>
                        <Divider style={{ margin: '12px 0' }} />
                        <Row style={{ margin: '8px 0' }}>
                          <Col span={12}><strong>Final Amount:</strong></Col>
                          <Col span={12} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                            {parseFloat(purchaseOrder.totalCost || 0).toLocaleString()}
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                  </Row>
                  
                  {purchaseOrder.currentStage?.toUpperCase() === 'PENDING' && (
                    <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                      <Button 
                        type="primary" 
                        onClick={showApproveModal}
                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                      >
                        Approve
                      </Button>
                      <Button 
                        type="primary"
                        danger 
                        onClick={showRejectModal}
                      >
                        Reject
                      </Button>
                    </div>
                  )}
                  
                  {/* Approve Modal */}
                  <Modal
                    title="Approve Purchase Order"
                    open={approveModalVisible}
                    onOk={handleApprove}
                    confirmLoading={approving}
                    onCancel={() => setApproveModalVisible(false)}
                    okText="Approve"
                    okButtonProps={{ style: { backgroundColor: '#52c41a', borderColor: '#52c41a' } }}
                  >
                    <p>Are you sure you want to approve this purchase order?</p>
                    <p>PO Reference: <strong>{purchaseOrder?.poReferance}</strong></p>
                    <p>Amount: <strong>{parseFloat(purchaseOrder?.totalCost || 0).toLocaleString()}</strong></p>
                  </Modal>

                  {/* Reject Modal */}
                  <Modal
                    title="Reject Purchase Order"
                    open={rejectModalVisible}
                    onOk={handleReject}
                    confirmLoading={rejecting}
                    onCancel={() => setRejectModalVisible(false)}
                    okText="Reject"
                    okButtonProps={{ danger: true }}
                  >
                    <p>Are you sure you want to reject this purchase order?</p>
                    <p>PO Reference: <strong>{purchaseOrder?.poReferance}</strong></p>
                    <p>Amount: <strong>{parseFloat(purchaseOrder?.totalCost || 0).toLocaleString()}</strong></p>
                  </Modal>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <p>Purchase order not found</p>
                </div>
              )}

            </Space>
        </div>
      </Content>
      <Footerdiv />
    </Layout>
  );
};

export default PurchaseOrderDetails;
